import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

import { audioConfig, spherePoints } from '../constants';

const animationConfig = {
    rotationSpeed: {
        x: 0.15,
        y: 0.1,
    },
    point: {
        sizeMultiplier: 1.5,
        distortionFactor: 1,
        distortionBase: 1,
        sizeBase: 1,
    },
    resetSpeed: 0.05,
} as const;

const sphereConfig = {
    pointsCount: 300,
    radius: 1.2,
    dimensions: 3,
    phiOffset: -1,
    distributionFactor: 2,
} as const;

const dimensionIndex = {
    x: 0,
    y: 1,
    z: 2,
} as const;

const generateSpherePoints = (count: number, radius: number): Float32Array => {
    const positions = new Float32Array(count * sphereConfig.dimensions);

    for (let i = 0; i < count; i++) {
        const phi = Math.acos(
            sphereConfig.phiOffset + (sphereConfig.distributionFactor * i) / count
        );
        const theta = Math.sqrt(count * Math.PI) * phi;

        positions[i * sphereConfig.dimensions + dimensionIndex.x] =
            radius * Math.cos(theta) * Math.sin(phi);
        positions[i * sphereConfig.dimensions + dimensionIndex.y] =
            radius * Math.sin(theta) * Math.sin(phi);
        positions[i * sphereConfig.dimensions + dimensionIndex.z] = radius * Math.cos(phi);
    }

    return positions;
};

export const useSpherePoints = (
    isListening: boolean,
    analyserRef: React.RefObject<AnalyserNode | null>
) => {
    const firstSphereRef = useRef<THREE.Points>(null);
    const secondSphereRef = useRef<THREE.Points>(null);
    const animationIntensityRef = useRef(0);

    const positions = useMemo(
        () => generateSpherePoints(sphereConfig.pointsCount, sphereConfig.radius),
        []
    );

    useFrame((state) => {
        const time = state.clock.getElapsedTime();

        if (firstSphereRef.current) {
            firstSphereRef.current.rotation.x = time * animationConfig.rotationSpeed.x;
            firstSphereRef.current.rotation.y = time * animationConfig.rotationSpeed.y;
        }

        if (secondSphereRef.current) {
            secondSphereRef.current.rotation.x = -time * animationConfig.rotationSpeed.x;
            secondSphereRef.current.rotation.y = -time * animationConfig.rotationSpeed.y;
        }

        if (isListening && analyserRef.current) {
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteFrequencyData(dataArray);

            const averageAmplitude =
                dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
            const normalizedAverage = averageAmplitude / audioConfig.maxFrequencyValue;

            animationIntensityRef.current +=
                (normalizedAverage - animationIntensityRef.current) * audioConfig.smoothingFactor;
        } else if (animationIntensityRef.current > 0) {
            animationIntensityRef.current -=
                animationConfig.resetSpeed * animationIntensityRef.current;
            if (animationIntensityRef.current < 0.001) animationIntensityRef.current = 0;
        }

        if (firstSphereRef.current?.material instanceof THREE.PointsMaterial) {
            firstSphereRef.current.material.size =
                spherePoints.firstSpherePointSize *
                (animationConfig.point.sizeBase +
                    animationIntensityRef.current * animationConfig.point.sizeMultiplier);
        }

        if (secondSphereRef.current?.material instanceof THREE.PointsMaterial) {
            secondSphereRef.current.material.size =
                spherePoints.secondSpherePointSize *
                (animationConfig.point.sizeBase +
                    animationIntensityRef.current * animationConfig.point.sizeMultiplier);
        }

        if (firstSphereRef.current?.geometry instanceof THREE.BufferGeometry) {
            const positions = firstSphereRef.current.geometry.attributes.position
                .array as Float32Array;
            const originalPositions = generateSpherePoints(
                sphereConfig.pointsCount,
                sphereConfig.radius
            );

            for (let i = 0; i < positions.length; i += sphereConfig.dimensions) {
                const originalX = originalPositions[i + dimensionIndex.x];
                const originalY = originalPositions[i + dimensionIndex.y];
                const originalZ = originalPositions[i + dimensionIndex.z];

                const distortionFactor =
                    animationConfig.point.distortionBase +
                    animationIntensityRef.current * animationConfig.point.distortionFactor;

                positions[i + dimensionIndex.x] = originalX * distortionFactor;
                positions[i + dimensionIndex.y] = originalY * distortionFactor;
                positions[i + dimensionIndex.z] = originalZ * distortionFactor;
            }

            firstSphereRef.current.geometry.attributes.position.needsUpdate = true;
        }

        if (secondSphereRef.current?.geometry instanceof THREE.BufferGeometry) {
            const positions = secondSphereRef.current.geometry.attributes.position
                .array as Float32Array;
            const originalPositions = generateSpherePoints(
                sphereConfig.pointsCount,
                sphereConfig.radius
            );

            for (let i = 0; i < positions.length; i += sphereConfig.dimensions) {
                const originalX = originalPositions[i + dimensionIndex.x];
                const originalY = originalPositions[i + dimensionIndex.y];
                const originalZ = originalPositions[i + dimensionIndex.z];

                const distortionFactor =
                    animationConfig.point.distortionBase +
                    animationIntensityRef.current * animationConfig.point.distortionFactor;

                positions[i + dimensionIndex.x] = originalX * distortionFactor;
                positions[i + dimensionIndex.y] = originalY * distortionFactor;
                positions[i + dimensionIndex.z] = originalZ * distortionFactor;
            }

            secondSphereRef.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    return { firstSphereRef, secondSphereRef, positions };
};
